import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Modal, Row, Col, Form } from 'react-bootstrap';
import { DataManager } from '../../../core/data-manager';
import { ApiManager } from '../../../core/api-manager';
import { LoginManager } from '../../../core/login-manager';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Swal from 'sweetalert2';
import moment from 'moment';
import { WriteXml } from 'fattura-elettronica';
import { settings } from '../../settings';

export default function DGLTH_invoice({ item, props, entity = null, refreshData }) {

    const navigate = useNavigate();
    const userInfo = LoginManager.getUserInfo();

    function previewInvoice() {
        navigate('/page/InvoicePreview?id=' + item.id);
    }

    function downloadXml() {
        if (!item.xml) {
            Swal.fire({ title: 'Attenzione', text: 'Nessun XML disponibile per questa fattura.', icon: 'warning', confirmButtonColor: '#ffc107' });
            return;
        }
        const xmlString = item.xml;
        const blob = new Blob([xmlString], { type: 'application/xml' });
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = `fattura_${item.invoiceNumber || item.id}.xml`;

        document.body.appendChild(a);
        a.click();
        a.remove();
    }

    // Funzione per estrarre solo i numeri dalla P.IVA (rimuove prefisso IT)
    function extractVatNumber(vatNumber) {
        if (!vatNumber) return '';
        // Rimuovi spazi e caratteri non numerici all'inizio (come IT)
        return vatNumber.replace(/^[A-Za-z]+/, '').trim();
    }

    // Funzione per validare/formattare il codice fiscale
    function formatCodiceFiscale(cf) {
        if (!cf) return '';
        // Rimuovi eventuali prefissi paese (IT) dal codice fiscale
        const cleaned = cf.replace(/^IT/i, '').trim();
        return cleaned;
    }

    async function generateXml() {
        if (!item.progressivo) {
            Swal.fire({ title: 'Attenzione', text: 'Inserire un progressivo per poter procedere.', icon: 'warning', confirmButtonColor: '#ffc107' });
            return;
        }

        try {
            const res = await DataManager.getItem('invoice', item.id);
            if (res.success !== 1 || !res.body || res.body.length === 0) {
                Swal.fire({ title: 'Errore', text: 'Impossibile recuperare i dati della fattura.', icon: 'error', confirmButtonColor: '#dc3545' });
                return;
            }

            const invoice = res.body[0];
            const dateNow = moment(invoice.invoiceDate || invoice.date).format('YYYY-MM-DD');
            const customer = invoice.idCustomerOrder?.idCustomer;

            // Prepara le righe (ordine corretto secondo schema SDI)
            const dettaglioLinee = [];
            if (invoice.rows && invoice.rows.length > 0) {
                invoice.rows.forEach((row, i) => {
                    const linea = {
                        NumeroLinea: i + 1,
                        Descrizione: row.description || row.name || 'Articolo',
                        Quantita: parseFloat(row.quantity || 1).toFixed(4),
                        UnitaMisura: 'PZ',
                        PrezzoUnitario: parseFloat(row.listPrice || 0).toFixed(2)
                    };

                    // Aggiungi sconto se presente (va prima di PrezzoTotale)
                    if (row.discount && row.discount !== '0' && row.discount !== '-') {
                        const sconti = [];
                        const parts = row.discount.split('+').map(p => p.trim());
                        parts.forEach(part => {
                            if (part.includes('%')) {
                                const perc = parseFloat(part.replace('%', '').trim());
                                if (!isNaN(perc) && perc > 0) {
                                    sconti.push({
                                        Tipo: 'SC',
                                        Percentuale: perc.toFixed(2)
                                    });
                                }
                            } else {
                                const importo = parseFloat(part);
                                if (!isNaN(importo) && importo > 0) {
                                    sconti.push({
                                        Tipo: 'SC',
                                        Importo: importo.toFixed(2)
                                    });
                                }
                            }
                        });
                        if (sconti.length > 0) {
                            linea.ScontoMaggiorazione = sconti;
                        }
                    }

                    // PrezzoTotale e AliquotaIVA vanno dopo ScontoMaggiorazione
                    linea.PrezzoTotale = parseFloat(row.price || 0).toFixed(2);
                    const rowAliquota = parseFloat(row.idVatCondition?.percentage || row.vat || 22);
                    linea.AliquotaIVA = rowAliquota.toFixed(2);
                    // Se AliquotaIVA è 0, Natura è obbligatoria per SDI
                    if (rowAliquota === 0) {
                        const natura = invoice.idVatCondition?.natura || invoice.idCustomerOrder?.idVatCondition?.natura;
                        if (natura) {
                            linea.Natura = natura;
                        }
                    }

                    dettaglioLinee.push(linea);
                });
            }

            // Costruisci DatiTrasmissione
            const datiTrasmissione = {
                IdTrasmittente: {
                    IdPaese: settings.dati_fattura?.statoPIVA || 'IT',
                    IdCodice: extractVatNumber(settings.dati_fattura?.PIVA) || '',
                },
                ProgressivoInvio: invoice.progressivo || item.progressivo,
                FormatoTrasmissione: settings.dati_fattura?.formatoTrasmissione || 'FPR12',
                CodiceDestinatario: customer?.sdi || settings.dati_fattura?.codiceDestinatario || '0000000'
            };
            // Aggiungi PECDestinatario solo se presente
            if (customer?.pec) {
                datiTrasmissione.PECDestinatario = customer.pec;
            }

            // Costruisci CedentePrestatore
            const cedentePrestatore = {
                DatiAnagrafici: {
                    IdFiscaleIVA: {
                        IdPaese: settings.dati_fattura?.statoPIVA || 'IT',
                        IdCodice: extractVatNumber(settings.dati_fattura?.PIVA) || '',
                    },
                    CodiceFiscale: formatCodiceFiscale(settings.dati_fattura?.codiceFiscale || settings.dati_fattura?.PIVA) || '',
                    Anagrafica: {
                        Denominazione: settings.dati_fattura?.denominazione || '',
                    },
                    RegimeFiscale: settings.dati_fattura?.regimeFiscale || 'RF01',
                },
                Sede: {
                    Indirizzo: settings.dati_fattura?.indirizzo || '',
                    CAP: settings.dati_fattura?.cap || '',
                    Comune: settings.dati_fattura?.comune || '',
                    Provincia: settings.dati_fattura?.provincia || '',
                    Nazione: settings.dati_fattura?.nazione || 'IT'
                }
            };
            // Aggiungi IscrizioneREA solo se i campi sono valorizzati
            if (settings.dati_fattura?.ufficio && settings.dati_fattura?.numeroREA) {
                cedentePrestatore.IscrizioneREA = {
                    Ufficio: settings.dati_fattura.ufficio,
                    NumeroREA: settings.dati_fattura.numeroREA
                };
                if (settings.dati_fattura?.socioUnico) {
                    cedentePrestatore.IscrizioneREA.SocioUnico = settings.dati_fattura.socioUnico;
                }
                if (settings.dati_fattura?.statoLiquidazione) {
                    cedentePrestatore.IscrizioneREA.StatoLiquidazione = settings.dati_fattura.statoLiquidazione;
                }
            }

            // Costruisci CessionarioCommittente
            const customerVatNumber = extractVatNumber(customer?.vatNumber);
            const customerCF = formatCodiceFiscale(customer?.taxCode);

            const cessionarioCommittente = {
                DatiAnagrafici: {
                    Anagrafica: {
                        Denominazione: customer?.name || '',
                    }
                },
                Sede: {
                    Indirizzo: customer?.address || '',
                    CAP: customer?.cap || customer?.postalCode || '00000',
                    Comune: customer?.city || customer?.locality || '',
                    Provincia: customer?.province || '',
                    Nazione: 'IT'
                }
            };

            // Aggiungi IdFiscaleIVA solo se P.IVA valida (11 caratteri numerici)
            if (customerVatNumber && customerVatNumber.length === 11) {
                cessionarioCommittente.DatiAnagrafici.IdFiscaleIVA = {
                    IdPaese: 'IT',
                    IdCodice: customerVatNumber
                };
            }
            // Aggiungi CodiceFiscale solo se valido (11-16 caratteri)
            if (customerCF && customerCF.length >= 11 && customerCF.length <= 16) {
                cessionarioCommittente.DatiAnagrafici.CodiceFiscale = customerCF;
            }

            const fattura = {
                FatturaElettronicaHeader: {
                    DatiTrasmissione: datiTrasmissione,
                    CedentePrestatore: cedentePrestatore,
                    CessionarioCommittente: cessionarioCommittente
                },
                FatturaElettronicaBody: [
                    {
                        DatiGenerali: {
                            DatiGeneraliDocumento: {
                                TipoDocumento: 'TD01',
                                Divisa: 'EUR',
                                Data: dateNow,
                                Numero: invoice.invoiceNumber || invoice.progressivo || '',
                                ImportoTotaleDocumento: parseFloat(invoice.invoiceTotal || 0).toFixed(2),
                                Causale: 'VENDITA'
                            },
                        },
                        DatiBeniServizi: {
                            DettaglioLinee: dettaglioLinee,
                            DatiRiepilogo: (() => {
                                const riepilogoAliquota = parseFloat(invoice.idVatCondition?.percentage || invoice.idCustomerOrder?.idVatCondition?.percentage || invoice.vat || 22);
                                const riepilogo = {
                                    AliquotaIVA: riepilogoAliquota.toFixed(2),
                                };
                                // Natura va dopo AliquotaIVA e prima di ImponibileImporto (ordine schema SDI)
                                if (riepilogoAliquota === 0) {
                                    const natura = invoice.idVatCondition?.natura || invoice.idCustomerOrder?.idVatCondition?.natura;
                                    if (natura) {
                                        riepilogo.Natura = natura;
                                    }
                                }
                                riepilogo.ImponibileImporto = parseFloat(invoice.invoiceImponibile || 0).toFixed(2);
                                riepilogo.Imposta = parseFloat(invoice.invoiceIva || 0).toFixed(2);
                                riepilogo.EsigibilitaIVA = 'I';
                                return [riepilogo];
                            })(),
                        }
                    },
                ],
            };

            const xmlString = WriteXml(fattura);

            const updateData = {
                xml: xmlString,
            };
            const resUpdate = await DataManager.updateItem('invoice', updateData, item.id);
            if (resUpdate.success === 1) {
                Swal.fire({ title: 'Completato', text: 'XML generato con successo.', icon: 'success', confirmButtonColor: '#198754' });
                refreshData();
            } else {
                Swal.fire({ title: 'Errore', text: 'Errore nel salvare il file XML.', icon: 'error', confirmButtonColor: '#dc3545' });
            }
        } catch (error) {
            Swal.fire({ title: 'Errore', text: error.message || 'Errore nella generazione XML.', icon: 'error', confirmButtonColor: '#dc3545' });
        }
    }

    async function markAsPaid() {
        const result = await Swal.fire({
            title: 'Conferma pagamento',
            text: 'Sei sicuro di voler confermare il pagamento di questa fattura?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#198754',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Sì, conferma',
            cancelButtonText: 'Annulla'
        });

        if (!result.isConfirmed) {
            return;
        }

        try {
            const updateData = {
                status: { value: '10_CONFIRMED', label: '10_CONFIRMED' }
            };
            const res = await DataManager.updateItem('invoice', updateData, item.id);
            if (res.success === 1) {
                const respCode = await ApiManager.sendAuthenticatedRequest('/custom/insert_invoice_internal_code', { id: item.id });
                if (respCode.success === 1) {
                    Swal.fire({
                        title: 'Confermata!',
                        text: 'Fattura confermata con successo. Progressivo: ' + (respCode.invoiceNumber || respCode.internalCode || ''),
                        icon: 'success',
                        confirmButtonColor: '#198754'
                    });
                } else {
                    Swal.fire({
                        title: 'Attenzione',
                        text: 'Fattura confermata ma errore nella generazione del progressivo.',
                        icon: 'warning',
                        confirmButtonColor: '#ffc107'
                    });
                }
                refreshData();
            } else {
                Swal.fire({
                    title: 'Errore',
                    text: 'Errore nella conferma della fattura.',
                    icon: 'error',
                    confirmButtonColor: '#dc3545'
                });
            }
        } catch (error) {
            Swal.fire({
                title: 'Errore',
                text: error.message || 'Errore nella conferma della fattura.',
                icon: 'error',
                confirmButtonColor: '#dc3545'
            });
        }
    }

    const isPending = !item.status || item.status.value === '0_PENDING';
    const isConfirmed = item.status && item.status.value === '10_CONFIRMED';
    const canManageXml = parseInt(userInfo.level) === 0 || parseInt(userInfo.level) === 1;

    if (entity !== null) {
        return (
            <>

                {isPending && (
                    <Button size="sm" variant="outline-success" onClick={() => markAsPaid()} className='me-1'>
                        Conferma
                    </Button>
                )}
                {canManageXml && isConfirmed && !item.xml && (
                    <Button size="sm" variant="outline-secondary" onClick={() => generateXml()} className='me-1'>
                        Genera XML
                    </Button>
                )}
                {canManageXml && item.xml && (
                    <Button size="sm" variant="outline-primary" onClick={() => downloadXml()} className='me-1'>
                        Download XML
                    </Button>
                )}
                <Button size="sm" variant="outline-secondary" onClick={() => previewInvoice()} className='me-1'>
                    <FontAwesomeIcon icon="print" />
                </Button>
            </>
        );
    } else {
        return <></>;
    }
}
